import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { useStateValue } from "../../context/store";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@material-ui/core";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import deLocale from "date-fns/locale/es";
import Utils from "../../utils/utils";

const useStyles2 = makeStyles({
  table: {
    minWidth: 500,
  },
  form: {
    width: "auto",
    position: "relative",
    paddingTop: "40px",
    marginLeft: -15,
  },
  button: {
    color: "blue",
    background: "#e7f103",
    "&:hover": { color: "white", background: "#0f70b7" },
  },
  buttonSearch: {
    color: "#ffffff",
    background: "#7EBDCE",
    "&:hover": { color: "white", background: "#099F9F" },
  },
  buttonClear: {
    color: "#0366D6",
    "&:hover": { background: "transparent" },
  },
  dropdown: {
    backgroundColor: "#FFFFFF",
    boxSizing: "border-box",
    borderRadius: "10px",
    fontSize: "14px",
    [`& fieldset`]: {
      borderRadius: 8,
      fontSize: 14,
    },
    "&  .MuiFormHelperText-root.Mui-error": {
      backgroundColor: "#E0F2F7",
      margin: 0,
      paddingLeft: 10,
    },
  },
  menuItem: {
    "&:hover": {
      borderLeft: "4px solid #098B8B",
    },
  },
});

const ClausesSearch = () => {
  const classes = useStyles2();

  const [{}, dispatch] = useStateValue();
  const [stateInitial, setStateInitial] = useState({
    name: "",
  });
  const errorFechaInferior = "Ingrese una fecha inferior a la fecha final";
  const errorFechaFinal = "Ingrese una fecha menor o igual a la actual";
  const [dateError, setDateError] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState("");
  const [dateFinalErrorMessage, setDateFinalErrorMessage] = useState("");
  const [dateFinalError, setDateFinalError] = useState(false);
  const [errorFechaInicial, setErrorFechaInicial] = useState(false);
  const [errorFechaFinalyup, setErrorFechaFinalyup] = useState(false);

  let currentDate = new Date();
  const [maxInitDate, setMaxInitDate] = useState(
    Utils.getFormattedDateMMddyyyy(currentDate, ".")
  );
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(
    Utils.getFormattedDateMMddyyyy(currentDate, ".")
  );

  const defaultValues = {
    name: null,
    initDate: null,
    endDate: null,
  };

  const dateMessage = "Formato de fecha inválido";
  const schema = yup.object().shape({
    name: yup.string().when([], {
      is: (name) => name && name.length > 0,
      then: yup.string(),
      otherwise: yup
        .string()
        .nullable(true)
        .notRequired()
        .transform((v) => (v ? v : null)),
    }),
    initDate: yup
      .date()
      .typeError(dateMessage)
      .when([], {
        is: (initDate) => initDate && initDate.length > 0,
        then: yup.date().typeError(dateMessage).nullable(true).notRequired(),
        otherwise: yup
          .date()
          .typeError(dateMessage)
          .nullable(true)
          .notRequired(),
      }),
    endDate: yup
      .date()
      .typeError("Formato de fecha inválido")
      .when([], {
        is: (endDate) => endDate && endDate.length > 0,
        then: yup
          .date()
          .typeError("Formato de fecha inválido")
          .nullable(true)
          .notRequired(),
        otherwise: yup
          .date()
          .typeError("Formato de fecha inválido")
          .nullable(true)
          .notRequired(),
      }),
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    errors,
    control,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "all",
    reValidateMode: "onChange",
  });

  const handleOnChange = (e) => {
    const { value, name } = e.target;
    setStateInitial({ ...stateInitial, [name]: value });
  };

  const clearForm = () => {
    setValue("name", null);
    setValue("initDate", null);
    setValue("endDate", null);
    setMinDate(null);
    setMaxInitDate(null);
    setDateError(false);
    setDateErrorMessage(null);
    setDateFinalError(false);
    setDateFinalErrorMessage(null);
  };

  const clearBtn = () => {
    clearForm();
    dispatch({
      type: "SET_FILTER_CLAUSE",
      filter: {
        name: null,
        initDate: null,
        endDate: null,
      },
      isFilter: true,
    });
  };

  const validateMaxInitDate = () => {
    var currentData = getValues();
    if (!currentData.endDate) {
      setMaxInitDate(Utils.getFormattedDateMMddyyyy(currentDate, "."));
    } else {
      setMaxInitDate(Utils.getFormattedDateMMddyyyy(currentData.endDate, "."));
    }
  };

  const onChangeDate = (value) => {
    setMinDate(value);
  };

  const updateMaxInitDate = (date) => {
    var currentData = getValues();
    if (!currentData.initDate) {
      setMaxInitDate(date);
    } else {
      setMaxInitDate(Utils.getFormattedDateMMddyyyy(currentDate, "."));
    }
  };

  const onSubmit = (data) => {
    if (data.name !== null || data.initDate !== null || data.endDate !== null) {
      if (data.initDate && !data.endDate) {
        data.endDate = new Date();
      } else if (data.initDate > data.endDate) {
        setDateError(true);
        setDateErrorMessage(errorFechaInferior);
        return;
      } else {
        setDateError(false);
        setDateErrorMessage(null);
      }
      dispatch({
        type: "SET_FILTER_CLAUSE",
        filter: data,
        isFilter: true,
      });
    }
  };

  useEffect(() => {
    const fechaInicial = watch("initDate");
    const fechaFinal = watch("endDate");
    if (fechaFinal > currentDate) {
      setDateFinalError(true);
      setDateFinalErrorMessage(errorFechaFinal);
    } else {
      setDateFinalError(false);
      setDateFinalErrorMessage(null);
    }

    if (fechaInicial && !fechaFinal) {
      if (fechaInicial > currentDate) {
        setDateError(true);
        setDateErrorMessage(errorFechaInferior);
      } else {
        setDateError(false);
        setDateErrorMessage(null);
      }
    } else if (fechaInicial > fechaFinal) {
      setDateError(true);
      setDateErrorMessage(errorFechaInferior);
    } else {
      setDateError(false);
      setDateErrorMessage(null);
    }
    if (fechaInicial) {
      setErrorFechaInicial(true);
    } else {
      setErrorFechaInicial(false);
    }
    if (fechaFinal) {
      setErrorFechaFinalyup(true);
    } else {
      setErrorFechaFinalyup(false);
    }
  }, [watch, currentDate]);

  return (
    <Card elevation={0} style={{ background: "#e0f2f7" }}>
      <CardContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classes.form}
          autocomplete="off"
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth size="small" shrink={true} notched={true}>
                <TextField
                  fullWidth
                  native
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  className={classes.dropdown}
                  onChange={handleOnChange}
                  inputRef={register}
                  type="text"
                  label="Nombre de cláusula"
                  name="name"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2} lg={2}>
              <MuiPickersUtilsProvider locale={deLocale} utils={DateFnsUtils}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      inputVariant="outlined"
                      variant="inline"
                      format="dd/MM/yyyy"
                      dateFormat="DD/MM/YYYY"
                      className={classes.dropdown}
                      label="Desde"
                      InputLabelProps={{ shrink: true }}
                      onAccept={onChangeDate}
                      onOpen={validateMaxInitDate}
                      error={
                        errorFechaInicial
                          ? !!errors.hasOwnProperty("initDate") &&
                            errors["initDate"].message
                          : null
                      }
                      helperText={
                        errorFechaInicial
                          ? errors.hasOwnProperty("initDate") &&
                            errors["initDate"].message
                          : null
                      }
                      minDate="01.01.2020"
                      maxDate={maxInitDate}
                    />
                  }
                  control={control}
                  name="initDate"
                  defaultValue={null}
                  placeholder="Desde"
                  size="small"
                />
              </MuiPickersUtilsProvider>
              <FormHelperText style={{ color: "#f44336" }}>
                {dateError && dateErrorMessage}
              </FormHelperText>
            </Grid>
            <Grid item xs={12} md={2} lg={2}>
              <MuiPickersUtilsProvider locale={deLocale} utils={DateFnsUtils}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      inputVariant="outlined"
                      variant="inline"
                      format="dd/MM/yyyy"
                      className={classes.dropdown}
                      dateFormat="DD/MM/YYYY"
                      label="Hasta"
                      InputLabelProps={{ shrink: true }}
                      onAccept={updateMaxInitDate}
                      minDate={minDate ? minDate : "01.01.2020"}
                      maxDate={maxDate}
                      error={
                        errorFechaFinalyup
                          ? !!errors.hasOwnProperty("endDate") &&
                            errors["endDate"].message
                          : null
                      }
                      helperText={
                        errorFechaFinalyup
                          ? errors.hasOwnProperty("endDate") &&
                            errors["endDate"].message
                          : null
                      }
                    />
                  }
                  control={control}
                  name="endDate"
                  defaultValue={null}
                  placeholder="Hasta"
                  size="small"
                />
              </MuiPickersUtilsProvider>
              <FormHelperText style={{ color: "#f44336" }}>
                {dateFinalError && dateFinalErrorMessage}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={3} lg={3}>
              <Button
                variant="contained"
                className={classes.buttonSearch}
                type="submit"
              >
                Buscar
              </Button>
              &nbsp;&nbsp;
              <Button
                variant="contained"
                className={classes.buttonSearch}
                onClick={clearBtn}
              >
                Limpiar
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default ClausesSearch;
